<template>
    <div class="pt-[20px] bg-[#ededed]">
        <div class="max-w-[1400px] w-full container mx-auto">
            <div class="pt-[60px] pr-[20px] pb-[2%] pl-[10px] max-w-[1400px]" id="offersNavigation">
                <offerNavigation />
            </div>
        </div>
    </div>
    <div class="max-w-[1400px] w-full container mx-auto">
        <v-breadcrumbs :items="breadcrumbs"></v-breadcrumbs>
        <div
            class="pt-[30px] pr-[20px] pb-[40px] pl-[10px] max-w-[1400px] text-center"
            id="offersForm"
        >
            <h2 class="text-left text-2xl font-light mb-[2%]">
                <strong>{{ $t('offers.createOfferTitle') }}</strong>
            </h2>
            <v-form @submit.prevent="submit" ref="formRef">
                <v-container fluid>
                    <FormInput
                        id="numStudents"
                        v-model="localNumStudents"
                        :rules="[rules.required, rules.number]"
                        :label="$t('offers.numStudentsLabel')"
                        required
                        @change="(e: any) => (localNumStudents = e.target.value)"
                        :disabled="isExistingOffer"
                    />
                    <FormInput
                        id="numTeachers"
                        v-model="localNumTeachers"
                        :rules="[rules.required, rules.number]"
                        :label="$t('offers.numTeachersLabel')"
                        required
                        @change="(e: any) => (localNumTeachers = e.target.value)"
                        :disabled="isExistingOffer"
                    />
                    <FormInput
                        id="numUserCorrection"
                        v-model="localNumUserCorrection"
                        :rules="[rules.number]"
                        :label="$t('offers.userCorrectionLabel')"
                        @change="(e: any) => (localNumUserCorrection = e.target.value)"
                        :disabled="isExistingOffer"
                    />
                    <FormSelect
                        id="schoolType"
                        v-model="localSelectedSchoolType"
                        :items="SchoolType"
                        :item-value="itemValue"
                        :item-text="itemText"
                        :item-title="itemTitle"
                        :rules="[rules.required]"
                        :label="$t('offers.schoolTypeLabel')"
                        required
                        @update:modelValue="onChangeSchoolType"
                        :disabled="isExistingOffer"
                    />
                    <FormSelect
                        id="detail"
                        v-model="localDetail"
                        :items="localSelectedSchoolType ? Details[localSelectedSchoolType] : []"
                        :item-value="itemValue"
                        :item-text="itemText"
                        :item-title="itemTitle"
                        :rules="[rules.required]"
                        :label="$t('offers.detailsLabel')"
                        required
                        @update:modelValue="filterContracts"
                        :disabled="isExistingOffer"
                    />
                    <FormSelect
                        id="canton"
                        v-model="localCanton"
                        :items="Canton"
                        :item-value="itemValue"
                        :item-text="itemText"
                        :item-title="itemTitle"
                        :rules="[rules.required]"
                        :label="$t('offers.cantonLabel')"
                        required
                        @update:modelValue="filterContracts"
                        :disabled="isExistingOffer"
                    />
                    <v-textarea
                        id="comments"
                        v-model="localComments"
                        :label="$t('offers.commentsLabel')"
                    ></v-textarea>
                    <v-radio-group
                        class="w-fit"
                        v-model="localContract"
                        row
                        :rules="isExistingOffer ? [] : [rules.requiredContract]"
                        :disabled="isExistingOffer"
                    >
                        <v-radio
                            :id="contract?.UUID"
                            v-for="contract in filteredContracts"
                            :key="contract?.UUID"
                            :label="String(contract?.LeadPortal_Name)"
                            :value="contract"
                            :disabled="isExistingOffer"
                        ></v-radio>
                    </v-radio-group>
                    <!-- Access Control Section for Offers role users -->
                    <div
                        v-if="hasOffersRole && thirdPartyPartners.length > 0"
                        class="mt-6 mb-4 p-4 border border-gray-200 rounded bg-gray-50"
                        data-cy="access-control-section"
                    >
                        <h3 class="text-left text-lg font-medium mb-3">
                            <strong>{{ $t('offers.accessControlTitle') }}</strong>
                        </h3>
                        <p class="text-left text-sm text-gray-600 mb-3">
                            {{ $t('offers.accessControlDescription') }}
                        </p>
                        <!-- Loop through each third party partner -->
                        <div
                            v-for="partner in thirdPartyPartners"
                            :key="partner.role_id"
                            class="mb-2"
                        >
                            <v-checkbox
                                v-model="localRoleAccess"
                                :value="partner.role_id.toString()"
                                :label="`${partner.partner_name} (${partner.role_name})`"
                                color="#db0720"
                                hide-details
                            />
                        </div>
                    </div>
                    <v-checkbox
                        v-model="localHasBeenSent"
                        color="#db0720"
                        :label="$t('offers.shippedLabel')"
                    />
                    <p class="text-left text-sm text-gray-500">
                        {{ $t('offers.note') }}
                    </p>
                </v-container>
                <FullButton
                    data-cy="submit"
                    :handleClick="submit"
                    width="100"
                    :title="$t('offers.saveButton')"
                    colour="white"
                />
            </v-form>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { ref, onMounted } from 'vue'
    import { useRoute } from 'vue-router'
    import FullButton from '@/components/Buttons/FullButton.vue'
    import offerNavigation from '@/components/SubNavigations/TheOfferNavigation.vue'
    import FormInput from '@/components/Offers/FormInput.vue'
    import FormSelect from '@/components/Offers/FormSelect.vue'
    import useOfferStore from '@/utils/useOfferStore'
    import { Canton, Details, SchoolType } from '@/constants/offers'
    import { SelectItem } from '@/model/types'
    import { ExtendedContract } from '@/model/types'
    import { useI18n } from 'vue-i18n'

    const route = useRoute()
    const formRef = ref(null)
    let isExistingOffer = ref(false)
    const { t } = useI18n()

    const {
        fetchContracts,
        updateOffer,
        createOffer,
        selectedOffer,
        contracts,
        signaturesList,
        offers,
        loading,
        fetchOfferAccess,
        updateOfferAccess,
        getHateoasLinks,
        hasLink,
        fetchThirdPartyPartners,
    } = useOfferStore()

    const localRoleAccess = ref<string[]>([])
    const hasOffersRole = ref(false)
    const thirdPartyPartners = ref<any[]>([])

    async function fetchDataOnMounted() {
        try {
            await fetchContracts()
            filterContracts()
            const links = await getHateoasLinks()
            hasOffersRole.value = hasLink('offer/access/update')

            // Only fetch third party partners if user has Offers role
            if (hasOffersRole.value) {
                thirdPartyPartners.value = await fetchThirdPartyPartners()

                // Load existing access if editing an offer
                if (isExistingOffer.value && selectedOffer.value?.id) {
                    try {
                        const accessData = await fetchOfferAccess(selectedOffer.value.id)
                        if (accessData && accessData.roles) {
                            localRoleAccess.value = accessData.roles.map((role: { role_id: any }) =>
                                String(role.role_id)
                            )
                        } else {
                            localRoleAccess.value = []
                        }
                    } catch (error) {
                        console.error('Error fetching offer access:', error)
                        localRoleAccess.value = []
                    }
                }
            }
        } catch (error) {
            console.error('Error initializing form data:', error)
        }
    }

    const localNumStudents = ref('')
    const localNumTeachers = ref('')
    const localNumUserCorrection = ref('')
    const localSelectedSchoolType = ref('')
    const localDetail = ref('')
    const localCanton = ref('')
    const localHasBeenSent = ref(false)
    const localComments = ref('')
    const localContract = ref({})
    const filteredContracts = ref<ExtendedContract[]>([])

    onMounted(() => {
        // Determine if this is an existing offer based on route
        const offerId = route.params.offerId || route.params.id?.toString().split('/').pop()
        isExistingOffer.value = !!(offerId && offerId !== '0' && selectedOffer.value?.id)

        // Initialize form values if editing existing offer
        if (isExistingOffer.value && selectedOffer.value) {
            localNumStudents.value = selectedOffer.value.numStudents || ''
            localNumTeachers.value = selectedOffer.value.numTeachers || ''
            localNumUserCorrection.value = selectedOffer.value.numUserCorrection || ''
            localSelectedSchoolType.value = selectedOffer.value.selectedSchoolType || ''
            localDetail.value = selectedOffer.value.detail || ''
            localCanton.value = selectedOffer.value.canton || ''
            localHasBeenSent.value = selectedOffer.value.hasBeenSent || false
            localComments.value = selectedOffer.value.comments || ''
            localContract.value = selectedOffer.value.contract || {}
        }

        fetchDataOnMounted()
    })

    const itemValue = (item: SelectItem) => item.key
    const itemText = (item: SelectItem) => item.value
    const itemTitle = (item: SelectItem) => item.value

    // Check route params to determine if this is a new or existing offer
    const offerId = route.params.offerId || route.params.id?.toString().split('/').pop()
    isExistingOffer.value = offerId && offerId !== '0' && selectedOffer.value?.id

    const rules = {
        required: (value: string) => !!value || t('offers.required'),
        requiredContract: (value: any) => {
            if (!value || typeof value !== 'object') return t('offers.required')
            return Object.keys(value).length > 0 || t('offers.required')
        },
        number: (value: string) => /^[\d]*$/.test(value) || t('offers.numberValidation'),
    }

    function filterContracts() {
        if (!contracts.value || !Array.isArray(contracts.value)) {
            console.error('Contracts data is not available')
            return
        }
        filteredContracts.value = contracts.value.filter((contract) => {
            return contract.Typ == localDetail.value && contract[localCanton.value] == '1'
        })
    }

    async function submit() {
        try {
            const isValid = formRef.value ? await (formRef.value as any).validate() : null
            if (isValid?.valid) {
                const offerData = {
                    numStudents: localNumStudents.value,
                    numTeachers: localNumTeachers.value,
                    numUserCorrection: localNumUserCorrection.value || 0,
                    schoolType: localSelectedSchoolType.value,
                    detail: localDetail.value,
                    canton: localCanton.value,
                    hasBeenSent: localHasBeenSent.value,
                    comments: localComments.value,
                    contract: localContract.value,
                }
                let offerId
                if (selectedOffer.value && selectedOffer.value.id) {
                    Object.assign(selectedOffer.value, offerData)
                    await updateOffer({
                        contactId: parseInt(route.params.id.toString()),
                        offerId: selectedOffer.value.id,
                        offer: selectedOffer.value,
                    })
                    offerId = selectedOffer.value.id
                } else {
                    const result = await createOffer({
                        contactId: parseInt(route.params.id.toString()),
                        offer: offerData,
                    })
                    offerId = result?.offer_id
                }

                // Update access control only if user has Offers role and we have a valid offer ID
                if (hasOffersRole.value && offerId) {
                    const roleIds = localRoleAccess.value
                        .filter((id) => id && id.trim() !== '')
                        .map((id) => parseInt(id))
                    await updateOfferAccess(offerId, roleIds)
                }
            }
        } catch (error) {
            console.error('Error during submission: ', error)
        }
    }

    function onChangeSchoolType() {
        localDetail.value = ''
        localCanton.value = ''
        filteredContracts.value = []
    }

    const breadcrumbs = [
        { title: 'Offerten', disabled: false, href: '/offers' },
        { title: 'Kontakt auswählen', disabled: false, href: '/offers/selectcontacts' },
        { title: 'Parameter eingeben', disabled: true, href: '/offers/createoffer/' },
    ]
</script>
